import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { AlertCircle, RefreshCw, Search, Eye, RotateCcw } from 'lucide-react'
import { supabase } from '../../supabase-client'
import { parseDateString } from '../../utils/dateUtils'

/**
 * XeroWebhookEventViewer Component
 *
 * Displays webhook events from xero_webhook_events table
 * Allows filtering, searching, and replaying failed events
 *
 * Follows Odin Style Guide:
 * - 90% neutral colors, 10% brand green
 * - Subtle interactions and transitions
 * - Clean white backgrounds with gentle shadows
 * - Professional, uncluttered aesthetic
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user from Supabase
 */
export default function XeroWebhookEventViewer({ user }) {
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const PAGE_SIZE = 50

  // Load events on mount and when filters change
  useEffect(() => {
    fetchEvents()
  }, [statusFilter, eventTypeFilter, page])

  /**
   * Fetch webhook events from Edge Function
   * Uses service_role access to bypass RLS restrictions
   */
  const fetchEvents = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Get session for auth header
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('Authentication required')
      }

      // Call Edge Function instead of direct query
      const { data, error } = await supabase.functions.invoke('xero-webhook-stats', {
        body: {
          action: 'list_events',
          page,
          statusFilter,
          eventTypeFilter,
          pageSize: PAGE_SIZE
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      setEvents(data.data || [])
      setTotalCount(data.count || 0)
    } catch (err) {
      console.error('[WebhookEventViewer] Failed to fetch events:', err)
      setError(err.message || 'Failed to load webhook events')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle search input
   */
  const handleSearch = () => {
    // Filter events locally by search term
    if (!searchTerm) {
      fetchEvents()
      return
    }

    const filtered = events.filter(
      (event) =>
        event.xero_resource_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.event_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.error_message &&
          event.error_message.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    setEvents(filtered)
  }

  /**
   * View event details
   */
  const handleViewEvent = (event) => {
    setSelectedEvent(event)
    setIsDialogOpen(true)
  }

  /**
   * Replay failed event
   * Calls the /replay endpoint on xero-webhook Edge Function
   */
  const handleReplayEvent = async (eventId) => {
    try {
      setError(null)

      // Get auth session for Supabase client
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call replay endpoint
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/xero-webhook/replay`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ eventId })
        }
      )

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to replay event')
      }

      // Refresh events to show updated status
      await fetchEvents()
    } catch (err) {
      console.error('[WebhookEventViewer] Failed to replay event:', err)
      setError(err.message || 'Failed to replay event')
    }
  }

  /**
   * Get status badge variant based on processing status
   */
  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'outline', label: 'Pending', className: 'border-gray-300 text-gray-700' },
      processing: { variant: 'outline', label: 'Processing', className: 'border-blue-300 text-blue-700' },
      completed: { variant: 'outline', label: 'Completed', className: 'border-green-300 text-green-700' },
      failed: { variant: 'outline', label: 'Failed', className: 'border-red-300 text-red-700' },
      ignored: { variant: 'outline', label: 'Ignored', className: 'border-gray-300 text-gray-500' }
    }

    const config = variants[status] || variants.pending

    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    )
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Format event type for display
   */
  const formatEventType = (eventCategory, eventType) => {
    return `${eventCategory}.${eventType}`
  }

  const hasNextPage = (page + 1) * PAGE_SIZE < totalCount
  const hasPrevPage = page > 0

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Webhook Events</CardTitle>
            <CardDescription>
              View and manage webhook events from Xero (last 30 days)
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchEvents}
            disabled={isLoading}
            className="hover:bg-gray-50 transition-colors duration-200"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <Input
              placeholder="Search by resource ID or error message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="border-gray-200 focus:border-gray-300 transition-colors duration-200"
            />
            <Button
              variant="outline"
              onClick={handleSearch}
              className="border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 border-gray-200">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>

          {/* Event Type Filter */}
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="w-40 border-gray-200">
              <SelectValue placeholder="Event Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="UPDATE">Invoice Update</SelectItem>
              <SelectItem value="CREATE">Payment Create</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Events Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="font-medium text-gray-700">Timestamp</TableHead>
                <TableHead className="font-medium text-gray-700">Event Type</TableHead>
                <TableHead className="font-medium text-gray-700">Resource ID</TableHead>
                <TableHead className="font-medium text-gray-700">Status</TableHead>
                <TableHead className="font-medium text-gray-700">Duration</TableHead>
                <TableHead className="font-medium text-gray-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading events...
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No webhook events found
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow
                    key={event.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <TableCell className="text-sm text-gray-700">
                      {formatDate(event.created_at)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">
                      {formatEventType(event.event_category, event.event_type)}
                    </TableCell>
                    <TableCell className="text-sm font-mono text-gray-600 max-w-xs truncate">
                      {event.xero_resource_id}
                    </TableCell>
                    <TableCell>{getStatusBadge(event.processing_status)}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {event.processing_duration_ms
                        ? `${event.processing_duration_ms}ms`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewEvent(event)}
                          className="hover:bg-gray-100 transition-colors duration-200"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {event.processing_status === 'failed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReplayEvent(event.id)}
                            className="hover:bg-gray-100 transition-colors duration-200"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {page * PAGE_SIZE + 1} to{' '}
            {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} events
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={!hasPrevPage}
              className="border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={!hasNextPage}
              className="border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Event Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Webhook Event Details</DialogTitle>
            <DialogDescription>
              Full details for webhook event
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-4">
              {/* Event Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Event ID</div>
                  <div className="text-sm font-mono text-gray-600 break-all">
                    {selectedEvent.event_id}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Status</div>
                  <div>{getStatusBadge(selectedEvent.processing_status)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Event Type</div>
                  <div className="text-sm text-gray-600">
                    {formatEventType(
                      selectedEvent.event_category,
                      selectedEvent.event_type
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Resource ID</div>
                  <div className="text-sm font-mono text-gray-600 break-all">
                    {selectedEvent.xero_resource_id}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Received At</div>
                  <div className="text-sm text-gray-600">
                    {formatDate(selectedEvent.created_at)}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    Processing Duration
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedEvent.processing_duration_ms
                      ? `${selectedEvent.processing_duration_ms}ms`
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Retry Count</div>
                  <div className="text-sm text-gray-600">{selectedEvent.retry_count}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Duplicate</div>
                  <div className="text-sm text-gray-600">
                    {selectedEvent.is_duplicate ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {selectedEvent.error_message && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Error Message</div>
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">
                    {selectedEvent.error_message}
                  </div>
                </div>
              )}

              {/* Raw Payload */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Raw Payload</div>
                <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                  {JSON.stringify(selectedEvent.raw_payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
